import { LitElement, html } from "lit-element";
import WebMidi from "webmidi";
import colorConvert from "color-convert";
import { scale } from "../utils/utils";
import { v4 as uuidv4 } from "uuid";

const Color = require("color");
class MidiIn extends LitElement {
  static get properties() {
    return {
      source: { type: String },
      output: { type: String },
      size: { type: Number },
      backgroundColor: { type: String },
      lowNote: { type: Number },
      highNote: { type: Number },
      startPosition: { type: String },
      noteOpacity: { type: Number },
      mode: { type: String },

      ccSource: { type: String },
      startColor: { type: String },
      endColor: { type: String },
      noteColor: { type: String },
      colorSystem: { type: String },
      colorValues: { type: Array },
      orientation: { type: String },
      width: { type: String },
      height: { type: String },
      top: { type: String },
      right: { type: String },
      bottom: { type: String },
      left: { type: String },

      widthDivision: { type: Number },
      heightDivision: { type: Number },
      offset: { type: Number },
      position: { type: String },
      opacity: { type: Number },
      mixBlendMode: { type: String },
      noteMixBlendMode: { type: String },
      ccResponsive: { type: Boolean },
      knobOneNumber: { type: Number },
      knobTwoNumber: { type: Number },
      knobThreeNumber: { type: Number },
      knobFourNumber: { type: Number },
      knobOneValue: { type: Number },
      knobTwoValue: { type: Number },
      knobThreeValue: { type: Number },
      knobFourValue: { type: Number },
      lightness: { type: Number },
      activeNotes: { type: Array },
      cycleColorValues: { type: Array },
      pitchDetection: { type: Boolean },
      opacityCC: { type: Number },
      ccSource: { type: String },
      boxShadow: { type: String },
      randomColors: { type: Array },
      name: { type: String },
      opacityIncrement: { type: Number },
    };
  }

  constructor() {
    super();

    // input
    this.source = null;
    this.output;
    this.ccSource = null;
    this.lowNote = 21;
    this.highNote = 108;

    // size
    this.size = 1;

    // color
    this.backgroundColor = "rgb(0, 0, 0";
    this.startColor = "blue";
    this.endColor = "red";
    this.noteColor = null;
    this.opacity = 1;
    this.noteOpacity = 1;
    this.boxShadow = "0";
    this.mixBlendMode = "normal";

    // responsive color
    this.colorValues = [0, -1, 0, 0];
    this.colorSystem = "cmyk";

    // position
    this.position = "absolute";
    this.orientation = "vertical";
    this.startPosition = "left";
    this.width = "100%";
    this.height = "100%";
    this.widthDivision = 1;
    this.heightDivision = 1;
    this.left = "0";
    this.top = "0";
    this.bottom = "0";
    this.right = "0";
    this.offset = 0;
    this.noteMixBlendMode = "normal";
    this.opacityIncrement = ".03";

    // note attributes
    // this.activeNotes = [];
    this.container = null;

    // midi sources
    this.midiSource;
    this.midiOutput;
    this.ccMidiSource;

    // web midi
    WebMidi.enable((error) => {
      if (error) {
        console.log(error);
      } else {
        this.midiSource = WebMidi.getInputByName(this.source);
        this.midiOutput = WebMidi.getOutputByName(this.output);
        this.ccMidiSource = WebMidi.getInputByName(this.ccSource);

        if (this.midiSource) {
          this.midiSource.addListener(
            "noteon",
            "all",
            this.handleNoteOn.bind(this)
          );
          this.midiSource.addListener(
            "noteoff",
            "all",
            this.handleNoteOff.bind(this)
          );
        }

        if (this.ccMidiSource) {
          this.ccMidiSource.addListener(
            "controlchange",
            "all",
            this.handleControlChange.bind(this)
          );
        }
      }
    });
  }

  handleNoteOn(e) {
    if (e.note.number >= this.lowNote && e.note.number <= this.highNote) {
      this.container = this.shadowRoot.querySelector("#container");
      const element = document.createElement("div");
      element.id = `note${e.note.number}`;

      const { note } = e;
      note.velocity = scale(e.velocity, 0, 1, 0, 127);
      element.style = this.getNoteStyle(note);

      // this.activeNotes.push(element);
      this.container.appendChild(element);
    }
  }

  handleNoteOff(e) {
    const elements = this.shadowRoot.querySelectorAll(`#note${e.note.number}`);
    elements.forEach((element) => {
      element.style.opacity = 0;
    });
    setTimeout(() => {
      for (const element of elements) {
        element.remove();
      }
    }, 1000);
  }

  handleControlChange(e) {
    this.midiOutput.sendPitchBend(scale(e.value, 0, 127, -1, 1), 1);
    // if (this.opacityCC === e.controller.number) {
    //   this.opacity = scale(e.value, 0, 127, 0, 1);
    // }
  }

  getNoteColorResponsive(note) {
    if (!note) {
      return;
    }

    let system = this.colorSystem;
    let rgb = "";
    let responsiveColor;

    responsiveColor = this.colorValues.map((val) => {
      if (val === -1) {
        if (system === "cmyk") {
          return Math.floor(scale(note.velocity, 0, 127, 0, 100));
        }
        if (system === "lab") {
          return Math.floor(scale(note.velocity, 0, 127, 0, 100));
        }
        if (system === "rgb") {
          return Math.floor(scale(note.velocity, 0, 127, 0, 255));
        }
      } else {
        return val;
      }
    });

    if (system === "rgb") {
      rgb = responsiveColor;
    } else if (system === "cmyk") {
      rgb = colorConvert.cmyk.rgb(responsiveColor);
    } else if (system === "lab") {
      rgb = colorConvert.lab.rgb(responsiveColor);
    }

    return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${1})`;
  }

  getNoteColor(note) {
    if (this.noteColor !== null) {
      return this.noteColor;
    }
    const factor = scale(note.number, this.lowNote, this.highNote - 1, 0, 1);
    const color = Color.hsl(this.startColor);
    const mixedColor = color.mix(Color.hsl(this.endColor), factor);
    return mixedColor;
  }

  getNoteStyle(note) {
    let style = ``;
    let posOffset;

    const containerHeight = window
      .getComputedStyle(this.container)
      .height.slice(0, -2);

    const containerWidth = window
      .getComputedStyle(this.container)
      .width.slice(0, -2);

    const offsetAxis =
      this.startPosition === "top" || this.startPosition === "bottom"
        ? containerHeight
        : containerWidth;

    switch (this.mode) {
      case "velocity-position":
        posOffset = scale(note.velocity, 0, 40, 0, offsetAxis);
        break;
      default:
        posOffset = scale(
          note.number - this.offset,
          this.lowNote,
          this.highNote,
          0,
          offsetAxis
        );
    }

    style = `
      position: absolute;
      ${this.startPosition}: ${posOffset}px;
      width: ${
        this.startPosition === "top" || this.startPosition === "bottom"
          ? containerWidth
          : (containerWidth / (this.highNote - this.lowNote)) * this.size
      };
      height: ${
        this.startPosition === "left" || this.startPosition === "right"
          ? containerHeight
          : (containerHeight / (this.highNote - this.lowNote)) * this.size
      };
      background: ${this.getNoteColor(note)};
      opacity: ${this.noteOpacity};
      transition: opacity .5s ease;
      mixBlendMode: ${this.noteMixBlendMode};`;

    return style;
  }

  render() {
    if (WebMidi.supported) {
      return html`
        <style>
          #container {
            transition: width 0.5s ease, height 0.5s ease, left 0.5s ease,
              top 0.5s ease;
            margin: 0 auto;
            position: ${this.position};
            display: inline-block;
            width: ${this.width
              ? this.width
              : `calc(100% / ${this.widthDivision})`};

            height: ${this.height
              ? this.height
              : `calc(100% / ${this.heightDivision})`};

            left: ${this.left};
            top: ${this.top};
            background: ${this.backgroundColor};
            opacity: ${this.opacity};
            box-shadow: ${this.boxShadow};
            mix-blend-mode: ${this.mixBlendMode};
            isolation: isolate;
          }
        </style>
        <div id="container"></div>
      `;
    } else {
      return html``;
    }
  }
}

customElements.define("midi-in", MidiIn);
